/**
 * Admin-preview request-auth resolver for the shared BaseService. Mirrors membership-hmi-preview
 * `http-common.ts#attachHttpRequestAuth` precedence so native host themes authenticate exactly like
 * the federated remote:
 *
 * 1. `builderIam` — raw agency IAM JWT, sent DIRECT to membership-backend as `source: WEB_USER`.
 * 2. `acatv2`     — exchanged IAM v2 admin envelope, sent through clientportal-middleware.
 * 3. `acat`       — legacy Firebase admin session, sent DIRECT to membership-backend.
 *
 * The precedence is applied when a tab enters / rehydrates a preview (`00.` courses admin-preview
 * middleware), which records the winning mode in this tab's `cp-admin-preview` record. Requests only
 * ever use the credential matching that record, and only while it is unexpired:
 *
 * - no record (learner tab, SSR)          → `none`: BaseService keeps the learner `cat` / `catv2` path.
 * - record + matching credential          → the admin mode.
 * - record but credential expired/cleared → `unavailable`: fail closed (no learner token, no learner
 *   refresh) so a preview tab never silently mixes learner data into the admin view.
 *
 * Reads sessions via the COMMON core-layer utils only — no import of the courses admin-preview layer
 * or auth store — so this stays dependency-free for every app.
 */
import {
  getAdminV2PreviewAccessToken,
  getAdminV2PreviewLocationId,
  isAdminPreviewTabRecordActive,
  readAdminPreviewTabRecord,
  readAdminV2PreviewEnvelope,
  type AdminPreviewTabMode,
  type AdminPreviewTabRecord,
  type AdminV2PreviewEnvelope,
} from '../../core-layer/utils/admin-preview-token.utils'
import {
  readBuilderIamPreviewCredential,
  readLegacyAdminPreviewCredential,
  type AdminPreviewCredential,
} from '../../core-layer/utils/admin-preview-auth.utils'

/**
 * Courses + membership backend path prefixes admin preview is scoped to, matched against the URL
 * PATHNAME from its start (optionally behind `/clientportal-middleware`). Covers the configured
 * bases: `MEMBERSHIPS_SERVER_URL` (`/memberships`), `MEMBERSHIP_SERVER_URL` (`/membership`) and the
 * `REST_API_URLS` courses services (`/courses/...`). Anchoring to the start keeps communities calls
 * such as `/communities/{loc}/groups/{g}/user/courses` and look-alikes (`/membership-settings`) out.
 */
const ADMIN_PREVIEW_API_PATH = /^(?:\/clientportal-middleware)?\/(?:courses|memberships?)(?:\/|$)/

/** Learner-scoped courses endpoints; a 403 there is a content-access answer, not an auth failure. */
const LEARNER_COURSES_API_PATH = /^(?:\/clientportal-middleware)?\/courses\/learners(?:\/|$)/

/** Pathname of an absolute or host-relative URL; empty when unparseable. */
function readRequestPathname(url: string): string {
  try {
    return new URL(url, 'http://localhost').pathname
  } catch {
    return ''
  }
}

/** Whether a request URL is in courses/membership scope for admin preview. */
export function isCoursesApiRequest(url: string | null | undefined): boolean {
  if (!url) return false
  return ADMIN_PREVIEW_API_PATH.test(readRequestPathname(url))
}

/**
 * Whether a rejected admin-preview request should mark the preview session failed: always on 401;
 * on 403 only for non-learner endpoints (a learner endpoint 403 is a per-content access decision).
 */
export function shouldReportAdminPreviewAuthFailure(status: number | undefined, url: string | null | undefined): boolean {
  if (status === 401) return true
  if (status !== 403 || !url) return false
  return !LEARNER_COURSES_API_PATH.test(readRequestPathname(url))
}

export type AdminPreviewAuthMode = AdminPreviewTabMode

export interface ResolvedAdminPreviewCredential {
  mode: AdminPreviewAuthMode
  token: string
  locationId: string | null
}

/** This tab is in a preview, but its admin credential is expired / cleared: send the request unauthenticated. */
export interface UnavailableAdminPreviewAuth {
  mode: 'unavailable'
  previewMode: AdminPreviewAuthMode
  locationId: string
}

export type ResolvedAdminPreviewAuth = ResolvedAdminPreviewCredential | UnavailableAdminPreviewAuth | { mode: 'none' }

/** Admin credentials available to this tab. Injected in tests; read from storage by default. */
export interface AdminPreviewAuthSources {
  /** This tab's preview record (expired or not); null when this tab is not in a preview. */
  record: AdminPreviewTabRecord | null
  envelope: AdminV2PreviewEnvelope | null
  builderIam: AdminPreviewCredential | null
  legacyAdmin: AdminPreviewCredential | null
}

const NO_ADMIN_SOURCES: AdminPreviewAuthSources = { record: null, envelope: null, builderIam: null, legacyAdmin: null }

/** Reads only the credential matching this tab's record mode (no record → nothing is decoded). */
export function readAdminPreviewAuthSources(): AdminPreviewAuthSources {
  const record = readAdminPreviewTabRecord()
  if (!record) return NO_ADMIN_SOURCES
  const active = isAdminPreviewTabRecordActive(record)
  return {
    record,
    envelope: active && record.mode === 'acatv2' ? readAdminV2PreviewEnvelope() : null,
    builderIam: active && record.mode === 'builderIam' ? readBuilderIamPreviewCredential(record) : null,
    legacyAdmin: active && record.mode === 'acat' ? readLegacyAdminPreviewCredential({ locationId: record.locationId }) : null,
  }
}

/** Modes that bypass clientportal-middleware and call membership-backend directly. */
export function isDirectAdminPreviewMode(mode: unknown): mode is 'builderIam' | 'acat' {
  return mode === 'builderIam' || mode === 'acat'
}

/** Modes that carry an admin credential (excludes `unavailable` / `none`). */
export function isAdminPreviewCredentialMode(mode: unknown): mode is AdminPreviewAuthMode {
  return mode === 'builderIam' || mode === 'acatv2' || mode === 'acat'
}

const NO_ADMIN_AUTH: ResolvedAdminPreviewAuth = { mode: 'none' }

/** The credential for this tab's record mode, honouring the remote's builder IAM → acatv2 → acat order. */
function resolveRecordCredential(record: AdminPreviewTabRecord, sources: AdminPreviewAuthSources): ResolvedAdminPreviewCredential | null {
  if (!isAdminPreviewTabRecordActive(record)) return null

  if (record.mode === 'builderIam' && sources.builderIam) {
    return { mode: 'builderIam', token: sources.builderIam.token, locationId: record.locationId }
  }

  const v2Token = record.mode === 'acatv2' ? getAdminV2PreviewAccessToken(sources.envelope) : null
  if (v2Token) {
    return { mode: 'acatv2', token: v2Token, locationId: getAdminV2PreviewLocationId(sources.envelope) }
  }

  if (record.mode === 'acat' && sources.legacyAdmin) {
    return { mode: 'acat', token: sources.legacyAdmin.token, locationId: record.locationId }
  }

  return null
}

/**
 * Resolve which admin-preview credential (if any) authenticates this request. The scope check runs
 * first so out-of-scope calls never touch storage.
 *
 * @param requestUrl full request URL.
 * @param sources admin credentials; defaults to this tab's storage reads (all null on SSR).
 */
export function resolveRequestAuth(requestUrl: string, sources?: AdminPreviewAuthSources): ResolvedAdminPreviewAuth {
  if (!isCoursesApiRequest(requestUrl)) return NO_ADMIN_AUTH

  const resolvedSources = sources ?? readAdminPreviewAuthSources()
  const { record } = resolvedSources
  if (!record) return NO_ADMIN_AUTH

  return (
    resolveRecordCredential(record, resolvedSources) ?? { mode: 'unavailable', previewMode: record.mode, locationId: record.locationId }
  )
}
